import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useToast } from 'context/ToastContext';
import { PilotApis } from 'apis/pilotAPI';
import type { ProjectEnvironment } from 'types/apis/pilotInterfaces';
import type {
  ConfigToolsHistoryActivity,
  ConfigToolsHistoryResponse,
  TargetEnvironment,
  TargetEntry,
} from 'types/apis/configToolsSuite';
import studioStyles from '../environments/StudioModal.module.scss';
import { formatLevelDisplay } from './utils/levelDisplay';
import {
  parseTargetEntry,
  parseTargetEnvironments,
  safeStringify,
  parseTimestamp,
  formatAbsoluteTimestamp,
  formatRelativeTimestamp,
  getHistoryUserLabel,
  formatHistoryStatusLabel,
} from './utils/historyParsers';
import { buildShellBundlePayload } from './utils/shellBundlePayload';

export interface PromotionHistoryProps {
  onBackToSelection: () => void;
  selectedPilot: { projectId: string; name?: string; projectName?: string } | null;
  targetEnv: ProjectEnvironment | null;
  compareTargetEnvironments: TargetEnvironment[];
  rcRef: React.MutableRefObject<any>;
}

function StudioButton({
  children,
  onClick,
  disabled,
  variant = 'primary',
  size = 'md',
  startIcon,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'outline';
  size?: 'sm' | 'md';
  startIcon?: React.ReactNode;
}) {
  const className =
    variant === 'primary'
      ? `${studioStyles.button} ${studioStyles.primaryButton}`
      : `${studioStyles.button} ${studioStyles.outlineButton}`;
  const style: React.CSSProperties | undefined =
    size === 'sm' ? { height: 32, padding: '0 12px', fontSize: 13 } : undefined;
  return (
    <button type="button" className={className} onClick={onClick} disabled={disabled} style={style}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {startIcon ? <span style={{ display: 'inline-flex', alignItems: 'center' }}>{startIcon}</span> : null}
        {children}
      </span>
    </button>
  );
}

export function PromotionHistory({
  onBackToSelection,
  selectedPilot,
  targetEnv,
  compareTargetEnvironments,
  rcRef,
}: PromotionHistoryProps) {
  const toast = useToast();

  const [historyActivities, setHistoryActivities] = useState<ConfigToolsHistoryActivity[]>([]);
  const [selectedHistoryActivity, setSelectedHistoryActivity] = useState<ConfigToolsHistoryActivity | null>(null);
  const [historyDetail, setHistoryDetail] = useState<any>(null);
  const [historyEventDetail, setHistoryEventDetail] = useState<any>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isLoadingHistoryDetail, setIsLoadingHistoryDetail] = useState(false);
  const [historyEnvId, setHistoryEnvId] = useState<string>('');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(25);
  const [historyLeftPaneCollapsed, setHistoryLeftPaneCollapsed] = useState(false);
  const [openHistoryCategories, setOpenHistoryCategories] = useState<string[]>([]);
  const [isDownloadingHistoryShellBundle, setIsDownloadingHistoryShellBundle] = useState(false);
  const [historyDetailSearch, setHistoryDetailSearch] = useState('');

  const historyTargetEnvironments = useMemo((): TargetEnvironment[] => {
    const statusDetail = historyDetail as any;
    const items = Array.isArray(statusDetail?.items) ? statusDetail.items : [];
    const firstItem = items.find((it: any) => Array.isArray(it?.targets) && it.targets.length > 0);
    const targetsRaw = firstItem?.targets ?? [];
    if (targetsRaw.length > 0) {
      const result = targetsRaw
        .map((t: any) => {
          const tg = t?.targetEnv;
          const id = tg?.id ?? t?.envId ?? t?.env_id;
          if (id == null) return null;
          return {
            id: Number(id),
            environmentName: String(tg?.environmentName ?? tg?.environment_name ?? tg?.name ?? ''),
            type: String(tg?.type ?? t?.type ?? ''),
            stackName: undefined,
          };
        })
        .filter((e: TargetEnvironment | null): e is TargetEnvironment => e != null);
      return result;
    }
    if (selectedHistoryActivity?.targetEnvironments?.length) {
      return selectedHistoryActivity.targetEnvironments.map((te) => ({
        id: Number(te.id),
        environmentName: te.environmentName ?? '',
        type: '',
        stackName: undefined,
      }));
    }
    const d = historyEventDetail as any;
    if (d) return parseTargetEnvironments(d);
    return [];
  }, [selectedHistoryActivity?.targetEnvironments, historyEventDetail, historyDetail]);

  const loadHistory = useCallback(
    async (envIdOverride?: number) => {
      const projectId = selectedPilot?.projectId;
      if (!projectId) return;
      const envId =
        envIdOverride != null
          ? envIdOverride
          : historyEnvId
            ? Number(historyEnvId)
            : targetEnv?.id
              ? Number(targetEnv.id)
              : undefined;

      setIsLoadingHistory(true);
      setHistoryError(null);
      setSelectedHistoryActivity(null);
      setHistoryDetail(null);
      setHistoryEventDetail(null);
      try {
        const historyParams = {
          projectId,
          ...(compareTargetEnvironments.length >= 2
            ? { targetEnvIds: compareTargetEnvironments.map((t) => t.id) }
            : envId != null
              ? { envId }
              : {}),
          page: historyPage,
          pageSize: historyPageSize,
        };
        const resp = await PilotApis.getConfigToolsHistory(rcRef.current, historyParams);
        if (resp.error) {
          const msg =
            typeof resp.error === 'string'
              ? resp.error
              : (resp.error.message || (resp.error.code ? `${resp.error.code}` : '') || 'Failed to load history');
          const suffix = typeof resp.error === 'object' && resp.error?.code ? ` (${resp.error.code})` : '';
          setHistoryError(`${msg}${suffix}`.trim());
          setHistoryActivities([]);
          return;
        }
        const data = resp.data as ConfigToolsHistoryResponse;
        const activities = Array.isArray((data as any)?.activities)
          ? (data as any).activities
          : Array.isArray((data as any)?.data?.activities)
            ? (data as any).data.activities
            : [];
        setHistoryActivities(activities);
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [
      selectedPilot?.projectId,
      targetEnv?.id,
      historyEnvId,
      compareTargetEnvironments,
      historyPage,
      historyPageSize,
      rcRef,
    ]
  );

  const loadHistoryDetail = useCallback(
    async (activity: ConfigToolsHistoryActivity) => {
      const eventId = activity?.eventIds?.[0];
      if (eventId === null || typeof eventId === 'undefined') return;
      setIsLoadingHistoryDetail(true);
      try {
        setHistoryEventDetail(null);
        try {
          const eventResp = await PilotApis.getConfigToolsHistoryEvent(rcRef.current, eventId);
          if (eventResp && !eventResp.error) {
            setHistoryEventDetail(eventResp.data || null);
          }
        } catch {
          /* API not implemented */
        }

        const resp = await PilotApis.getPromotionStatus(rcRef.current, String(eventId));
        if (resp.error) {
          setHistoryDetail({ error: resp.error });
          return;
        }
        const data = resp.data || null;
        setHistoryDetail(data);
      } finally {
        setIsLoadingHistoryDetail(false);
      }
    },
    [rcRef]
  );

  const downloadHistoryShellBundle = useCallback(
    async (shellBundleUrlOverride?: string) => {
      if (!selectedHistoryActivity) {
        toast.error('No history activity selected.');
        return;
      }

      const eventId = selectedHistoryActivity?.eventIds?.[0];
      if (eventId === null || typeof eventId === 'undefined') {
        toast.error('No event ID available for this activity.');
        return;
      }

      setIsDownloadingHistoryShellBundle(true);
      try {
        // Use existing historyDetail if available, otherwise fetch status response
        let statusData: any = null;
        if (historyDetail && !(historyDetail as any)?.error) {
          statusData = historyDetail;
        } else {
          // Get the status response which contains items and environment info
          const statusResp = await PilotApis.getPromotionStatus(rcRef.current, String(eventId));
          if (statusResp.error) {
            const msg =
              typeof statusResp.error === 'string'
                ? statusResp.error
                : (statusResp.error?.message || statusResp.error?.code || 'Failed to fetch sync status');
            toast.error(msg);
            return;
          }
          statusData = statusResp.data || {};
        }

        if (!statusData) {
          toast.error('No status data available.');
          return;
        }
        const statusItems = Array.isArray(statusData.items) ? statusData.items : [];

        if (statusItems.length === 0) {
          toast.error('No items found in status response.');
          return;
        }

        // Extract projectId, sourceEnvId, and targetEnvironments from status response
        const projectId = 
          statusData.projectId ?? 
          selectedHistoryActivity?.projectId ?? 
          selectedPilot?.projectId;
        
        const sourceEnvId = 
          statusData.sourceEnvId ?? 
          statusData.source_env_id ??
          statusData.sourceEnv?.id ??
          selectedHistoryActivity?.sourceEnv?.id;

        if (!projectId || !sourceEnvId) {
          toast.error('Missing project ID or source environment ID. Cannot create shell bundle.');
          return;
        }

        // Extract target environments from status response or use historyTargetEnvironments
        let targetEnvs: TargetEnvironment[] = [];
        if (statusData.targetEnvironments && Array.isArray(statusData.targetEnvironments)) {
          targetEnvs = statusData.targetEnvironments.map((te: any) => ({
            id: Number(te.id),
            environmentName: String(te.environmentName ?? te.environment_name ?? ''),
            type: String(te.type ?? ''),
            stackName: te.stack_name != null ? String(te.stack_name) : te.stackName != null ? String(te.stackName) : undefined,
          }));
        } else if (historyTargetEnvironments.length > 0) {
          targetEnvs = historyTargetEnvironments;
        } else if (selectedHistoryActivity.targetEnvironments && selectedHistoryActivity.targetEnvironments.length > 0) {
          targetEnvs = selectedHistoryActivity.targetEnvironments.map((te) => ({
            id: Number(te.id),
            environmentName: te.environmentName ?? '',
            type: '',
            stackName: undefined,
          }));
        }

        if (targetEnvs.length === 0) {
          toast.error('No target environments found. Cannot create shell bundle.');
          return;
        }

        // Build shell bundle payload from status items
        const shellBundlePayload = buildShellBundlePayload(
          statusItems,
          targetEnvs,
          Number(sourceEnvId),
          String(projectId)
        );
        
        if (!shellBundlePayload.items || shellBundlePayload.items.length === 0) {
          toast.error('No items could be processed from status response. Please check the status data structure.');
          return;
        }

        // Download shell bundle using the download API with payload
        const resp = await PilotApis.downloadShellBundle(rcRef.current, shellBundlePayload);

        if (resp.error) {
          const msg =
            typeof resp.error === 'string'
              ? resp.error
              : (resp.error.message || (resp.error.code ? `${resp.error.code}` : '') || 'Create shell bundle failed');
          const suffix = typeof resp.error === 'object' && resp.error?.code ? ` (${resp.error.code})` : '';
          toast.error(`${msg}${suffix}`.trim());
          return;
        }

        if (!resp.data) {
          toast.error('No data received from server.');
          return;
        }

        // Ensure we have a Blob
        let zipBlob: Blob | null = null;
        if (resp.data instanceof Blob) {
          zipBlob = resp.data;
        } else if (resp.data && typeof (resp.data as any).byteLength === 'number') {
          // Check if it's an ArrayBuffer-like object
          zipBlob = new Blob([resp.data as ArrayBuffer], { type: 'application/zip' });
        } else if (typeof resp.data === 'string') {
          try {
            const parsed = JSON.parse(resp.data);
            if (parsed.error || parsed.message) {
              throw new Error(parsed.error || parsed.message || 'Server returned error');
            }
          } catch (e) {
            if (e instanceof Error && e.message.includes('Server returned error')) {
              throw e;
            }
            zipBlob = new Blob([resp.data], { type: 'application/zip' });
          }
        } else {
          zipBlob = new Blob([resp.data], { type: 'application/zip' });
        }

        if (!zipBlob) {
          toast.error('Failed to process zip file from server.');
          return;
        }

        if (zipBlob.size === 0) {
          toast.error('Received empty zip file from server.');
          return;
        }

        // Extract filename from Content-Disposition header
        let filename = `shell-bundle-${eventId}-${Date.now()}.zip`;
        if (resp.headers) {
          const contentDisposition =
            resp.headers['content-disposition'] ||
            resp.headers['Content-Disposition'] ||
            (typeof resp.headers.get === 'function' ? resp.headers.get('content-disposition') : null);

          if (contentDisposition && typeof contentDisposition === 'string') {
            const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (match && match[1]) {
              filename = match[1].replace(/['"]/g, '').trim();
            }
          }
        }

        // Download the zip file
        const link = document.createElement('a');
        link.href = URL.createObjectURL(zipBlob);
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

        toast.success(`Downloaded: ${filename}`);
      } catch (err) {
        console.error('Download shell bundle failed:', err);
        toast.error(err instanceof Error ? err.message : 'Download failed');
      } finally {
        setIsDownloadingHistoryShellBundle(false);
      }
    },
    [selectedHistoryActivity, selectedPilot, historyTargetEnvironments, historyDetail, toast, rcRef]
  );

  const historyDetailItems = useMemo(() => {
    const fromEventDetail = (() => {
      const d = historyEventDetail as any;
      if (!d) return [] as any[];

      if (d?.changes && typeof d.changes === 'object') {
        const items: any[] = [];
        Object.entries(d.changes).forEach(([category, arr]) => {
          if (!Array.isArray(arr)) return;
          arr.forEach((it: any) => {
            const targetsRaw = Array.isArray(it?.targets) ? it.targets : [];
            const targets: TargetEntry[] = targetsRaw
              .map((e: any) => parseTargetEntry(e))
              .filter((entry: TargetEntry | null): entry is TargetEntry => entry != null);
            const firstTarget = targets.length > 0 ? targets[0] : null;
            const metadataForLevel = it?.metadata ?? { level: it?.level, levelDisplay: it?.levelDisplay };
            items.push({
              category,
              configType: it?.configType ?? it?.metadata?.configType ?? category,
              key: it?.metadata?.configKey ?? it?.key ?? '',
              level: formatLevelDisplay(metadataForLevel, '—'),
              oldValue: it?.sourceValue ?? it?.oldValue ?? it?.currentValue,
              newValue: firstTarget ? firstTarget.targetValue : (it?.newValue ?? it?.targetValue),
              status: firstTarget ? firstTarget.status : it?.status,
              error: it?.error,
              targets: targets.length > 0 ? targets : undefined,
            });
          });
        });
        return items;
      }

      const itemsArr = Array.isArray(d.items) ? d.items : Array.isArray(d.event?.items) ? d.event.items : Array.isArray(d.data?.items) ? d.data.items : null;
      if (itemsArr) {
        return itemsArr.map((it: any) => {
          const targetsRaw = Array.isArray(it?.targets) ? it.targets : [];
          const targets: TargetEntry[] = targetsRaw
            .map((e: any) => parseTargetEntry(e))
            .filter((entry: TargetEntry | null): entry is TargetEntry => entry != null);
          const firstTarget = targets.length > 0 ? targets[0] : null;
          const metadataForLevel = it?.metadata ?? { level: it?.level, levelDisplay: it?.levelDisplay };
          return {
            category: it?.category ?? '',
            configType: it?.metadata?.configType ?? it?.configType ?? it?.category ?? '',
            key: it?.metadata?.configKey ?? it?.key ?? '',
            level: formatLevelDisplay(metadataForLevel, '—'),
            oldValue: it?.sourceValue ?? it?.oldValue ?? it?.currentValue,
            newValue: firstTarget ? firstTarget.targetValue : (it?.newValue ?? it?.targetValue),
            status: firstTarget ? firstTarget.status : it?.status,
            error: it?.error,
            targets: targets.length > 0 ? targets : undefined,
          };
        });
      }
      return [] as any[];
    })();

    const statusDetail = historyDetail as any;
    const hasStatusItemsWithTargets = Array.isArray(statusDetail?.items) && statusDetail.items.some(
      (it: any) => Array.isArray(it?.targets) && it.targets.length > 0
    );
    const useEventDetail = !hasStatusItemsWithTargets && fromEventDetail.some((it: any) => typeof it?.oldValue !== 'undefined' || typeof it?.newValue !== 'undefined');
    if (useEventDetail) {
      return fromEventDetail;
    }

    const fromStatusDetail = Array.isArray(statusDetail?.items)
      ? statusDetail.items.map((it: any) => {
          const targetsRaw = Array.isArray(it?.targets) ? it.targets : [];
          const targets: TargetEntry[] = targetsRaw
            .map((e: any) => parseTargetEntry(e))
            .filter((entry: TargetEntry | null): entry is TargetEntry => entry != null);
          // Keep targets as-is; getTargetValue will match by envId, environmentName, or index
          const firstTarget = targets.length > 0 ? targets[0] : null;
          const metadataForLevel = it?.metadata ?? { level: it?.level, levelDisplay: it?.levelDisplay ?? (it as any)?.level_display };
          return {
            category: it?.category ?? '',
            configType: it?.metadata?.configType ?? it?.configType ?? it?.category ?? '',
            key: it?.metadata?.configKey ?? it?.key ?? it?.configKey ?? '',
            level: formatLevelDisplay(metadataForLevel, '—'),
            oldValue: it?.sourceValue ?? it?.source_value ?? it?.oldValue,
            newValue: firstTarget ? firstTarget.targetValue : (it?.targetValue ?? it?.newValue),
            status: firstTarget ? firstTarget.status : it?.status,
            error: it?.error,
            targets: targets.length > 0 ? targets : undefined,
            metadata: it?.metadata,
          };
        })
      : [];

    return fromStatusDetail;
  }, [historyDetail, historyEventDetail, historyTargetEnvironments]);

  const historyItemsByCategory = useMemo(() => {
    const map: Record<string, any[]> = {};
    (historyDetailItems || []).forEach((it: any) => {
      const cat = String(it?.category || 'configs');
      if (!map[cat]) map[cat] = [];
      map[cat].push(it);
    });
    return map;
  }, [historyDetailItems]);

  const allHistoryCategories = useMemo(() => Object.keys(historyItemsByCategory), [historyItemsByCategory]);

  const historyFilteredItemsByCategory = useMemo(() => {
    const q = historyDetailSearch.trim().toLowerCase();
    if (!q) return historyItemsByCategory;
    const map: Record<string, any[]> = {};
    Object.entries(historyItemsByCategory).forEach(([cat, list]) => {
      const filtered = list.filter((it: any) => {
        const parts: string[] = [
          (it?.metadata as any)?.stringKey ?? '',
          it?.key ?? '',
          it?.configType ?? it?.category ?? '',
          it?.level ?? '',
          typeof it?.oldValue !== 'undefined' ? safeStringify(it.oldValue) : '',
          typeof it?.newValue !== 'undefined' ? safeStringify(it.newValue) : '',
          formatHistoryStatusLabel(it?.status),
          it?.status ?? '',
          it?.error ?? '',
        ];
        const targetsArr = (it?.targets as TargetEntry[] | undefined) ?? [];
        targetsArr.forEach((t: TargetEntry) => {
          parts.push(t?.environmentName ?? '');
          parts.push(typeof t?.oldValue !== 'undefined' ? safeStringify(t.oldValue) : '');
          parts.push(typeof t?.targetValue !== 'undefined' ? safeStringify(t.targetValue) : '');
        });
        const searchable = parts.join(' ').toLowerCase();
        return searchable.includes(q);
      });
      if (filtered.length > 0) map[cat] = filtered;
    });
    return map;
  }, [historyItemsByCategory, historyDetailSearch]);

  const visibleHistoryCategories = useMemo(
    () => (historyDetailSearch.trim() ? Object.keys(historyFilteredItemsByCategory) : allHistoryCategories),
    [historyDetailSearch, historyFilteredItemsByCategory, allHistoryCategories]
  );

  useEffect(() => {
    if (targetEnv?.id) {
      setHistoryEnvId(String(targetEnv.id));
      loadHistory(Number(targetEnv.id));
    } else {
      loadHistory();
    }
  }, [targetEnv?.id, historyPage, loadHistory]);

  useEffect(() => {
    if (!Array.isArray(historyActivities) || historyActivities.length === 0) return;
    const first = historyActivities[0];
    if (selectedHistoryActivity && (selectedHistoryActivity.activityId === first.activityId || selectedHistoryActivity.eventIds?.[0] === first.eventIds?.[0])) return;
    setSelectedHistoryActivity(first);
    loadHistoryDetail(first);
  }, [historyActivities]);

  return (
    <Card sx={{ borderRadius: 2, boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
      <CardHeader
        sx={{ textAlign: 'left' }}
        title={<Typography sx={{ fontWeight: 700 }}>Full Update History</Typography>}
        subheader={
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="body2" color="text.secondary">
              Chronological log of synchronizations between these environments.
            </Typography>
          </Box>
        }
        action={
          <StudioButton variant="outline" onClick={onBackToSelection} startIcon={<ChevronLeftIcon sx={{ fontSize: 18 }} />}>
            Back to Selection
          </StudioButton>
        }
      />
      <CardContent>
        {historyError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {historyError}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              lg: historyLeftPaneCollapsed ? '32px 1fr' : '1fr 2fr',
            },
            gap: 2,
            alignItems: 'start',
          }}
        >
          {/* Left: History list */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              minWidth: 0,
              ...(historyLeftPaneCollapsed && { display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start', minWidth: 32, maxWidth: 32 }),
            }}
          >
            {historyLeftPaneCollapsed ? (
              <Tooltip title="Show history list">
                <IconButton
                  size="small"
                  onClick={() => setHistoryLeftPaneCollapsed(false)}
                  sx={{ m: 0.5, alignSelf: 'center' }}
                  aria-label="Show history list"
                >
                  <ChevronRightIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <>
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'left', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 18 }}>Push History</Typography>
                    <Typography variant="body2" color="text.secondary">Select a push event to view its details.</Typography>
                  </Box>
                  <Tooltip title="Hide list to expand details">
                    <IconButton size="small" onClick={() => setHistoryLeftPaneCollapsed(true)} sx={{ flexShrink: 0, mt: -0.5 }} aria-label="Hide history list">
                      <ChevronLeftIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box sx={{ maxHeight: '60vh', overflow: 'auto', p: 1 }}>
                  {isLoadingHistory ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                      <CircularProgress size={16} /> Loading history…
                    </Typography>
                  ) : (historyActivities || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                      No history activities found.
                    </Typography>
                  ) : (
                    (historyActivities || []).map((activity, idx) => {
                      const rowKey = activity.activityId ?? activity.eventIds?.[0] ?? idx;
                      const selected =
                        selectedHistoryActivity?.activityId === activity.activityId ||
                        (selectedHistoryActivity?.eventIds?.[0] === activity.eventIds?.[0] && !activity.activityId);
                      const d = parseTimestamp(activity.timestamp);
                      const abs = d ? formatAbsoluteTimestamp(d) : (activity.timestamp || '-');
                      const rel = d ? formatRelativeTimestamp(d) : '-';
                      const statusLower = String(activity.status || '').toLowerCase();
                      const statusStyles =
                        statusLower === 'success'
                          ? { bg: 'rgba(46, 125, 50, 0.12)', fg: 'success.main', border: 'rgba(46, 125, 50, 0.45)' }
                          : statusLower === 'partial_success'
                            ? { bg: 'rgba(255, 152, 0, 0.12)', fg: 'warning.main', border: 'rgba(255, 152, 0, 0.45)' }
                            : statusLower === 'failed'
                              ? { bg: 'rgba(211, 47, 47, 0.10)', fg: 'error.main', border: 'rgba(211, 47, 47, 0.45)' }
                              : { bg: 'transparent', fg: 'text.secondary', border: 'divider' };
                      const srcName = (activity.sourceEnv as any)?.environmentName || (activity.sourceEnv as any)?.name || '—';
                      const tgtName =
                        activity.targetEnvironments?.length > 0
                          ? activity.targetEnvironments.map((t) => t.environmentName || `Env ${t.id}`).join(', ')
                          : '—';

                      return (
                        <Box
                          key={String(rowKey)}
                          component="button"
                          type="button"
                          onClick={() => {
                            setSelectedHistoryActivity(activity);
                            loadHistoryDetail(activity);
                          }}
                          sx={{
                            width: '100%',
                            textAlign: 'left',
                            p: 1.5,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: selected ? 'rgba(0, 120, 212, 0.35)' : 'divider',
                            bgcolor: selected ? 'rgba(0, 120, 212, 0.08)' : 'transparent',
                            cursor: 'pointer',
                            mb: 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', mb: 0.75 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 999, border: '1px solid', borderColor: 'divider', fontSize: 12, fontWeight: 600, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={srcName}>
                                {srcName}
                              </Box>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>→</Typography>
                              <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 999, border: '1px solid', borderColor: 'divider', fontSize: 12, fontWeight: 600, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tgtName}>
                                {tgtName}
                              </Box>
                            </Box>
                            <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 999, border: '1px solid', borderColor: statusStyles.border, bgcolor: statusStyles.bg, color: statusStyles.fg, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {formatHistoryStatusLabel(activity.status)}
                            </Box>
                          </Box>
                          <Tooltip title={abs}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                              by {String(getHistoryUserLabel(activity))} &bull; {rel}
                            </Typography>
                          </Tooltip>
                        </Box>
                      );
                    })
                  )}
                </Box>

                <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Page {historyPage}</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <StudioButton variant="outline" size="sm" disabled={historyPage <= 1 || isLoadingHistory} onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}>
                      Prev
                    </StudioButton>
                    <StudioButton variant="outline" size="sm" disabled={isLoadingHistory || (historyActivities || []).length < historyPageSize} onClick={() => setHistoryPage((p) => p + 1)}>
                      Next
                    </StudioButton>
                  </Box>
                </Box>
              </>
            )}
          </Box>

          {/* Right: Details */}
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            {!selectedHistoryActivity ? (
              <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">Select a history item to view details.</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'left' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                        Details for Push to{' '}
                        {selectedHistoryActivity.targetEnvironments?.length > 0
                          ? selectedHistoryActivity.targetEnvironments.map((t) => t.environmentName || `Env ${t.id}`).join(', ')
                          : 'Target'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(() => {
                          const d = parseTimestamp(selectedHistoryActivity.timestamp);
                          const abs = d ? formatAbsoluteTimestamp(d) : (selectedHistoryActivity.timestamp || '-');
                          return `${abs} by ${String(getHistoryUserLabel(selectedHistoryActivity))}`;
                        })()}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                      <Tooltip title={historyLeftPaneCollapsed ? 'Show history list' : 'Hide history list'}>
                        <IconButton size="small" onClick={() => setHistoryLeftPaneCollapsed(!historyLeftPaneCollapsed)} aria-label={historyLeftPaneCollapsed ? 'Show history list' : 'Hide history list'}>
                          {historyLeftPaneCollapsed ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </Tooltip>
                      <StudioButton
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const isAllExpanded = visibleHistoryCategories.length > 0 && openHistoryCategories.length === visibleHistoryCategories.length;
                          setOpenHistoryCategories(isAllExpanded ? [] : visibleHistoryCategories);
                        }}
                        disabled={allHistoryCategories.length === 0}
                      >
                        {visibleHistoryCategories.length > 0 && openHistoryCategories.length === visibleHistoryCategories.length ? 'Collapse All' : 'Expand All'}
                      </StudioButton>
                      {selectedHistoryActivity?.eventType === 'shell_bundle' ? (
                        <StudioButton
                          variant="outline"
                          size="sm"
                          onClick={() => downloadHistoryShellBundle()}
                          disabled={isDownloadingHistoryShellBundle}
                          startIcon={isDownloadingHistoryShellBundle ? <CircularProgress size={14} /> : undefined}
                        >
                          Download PR
                        </StudioButton>
                      ) : null}
                      <StudioButton variant="outline" size="sm" disabled={!selectedHistoryActivity || isLoadingHistoryDetail} onClick={() => loadHistoryDetail(selectedHistoryActivity)}>
                        Refresh status
                      </StudioButton>
                    </Box>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Search in all columns (key, level, values, status…)"
                    value={historyDetailSearch}
                    onChange={(e) => setHistoryDetailSearch(e.target.value)}
                    sx={{
                      mt: 1.5,
                      width: '100%',
                      maxWidth: 480,
                      textAlign: 'left',
                      '& .MuiInputBase-input': { textAlign: 'left' },
                      '& .MuiFormHelperText-root': { textAlign: 'left' },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: historyDetailSearch ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setHistoryDetailSearch('')}
                            aria-label="Clear search"
                            sx={{ p: 0.5 }}
                          >
                            <ClearIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                    inputProps={{ 'aria-label': 'Search in all columns' }}
                    helperText={historyDetailSearch ? `${visibleHistoryCategories.reduce((acc, c) => acc + (historyFilteredItemsByCategory[c]?.length ?? 0), 0)} matching rows` : 'Searches config key, level, type, values, status, and errors'}
                  />
                </Box>

                <Box sx={{ p: 2 }}>
                  {isLoadingHistoryDetail ? (
                    <Typography variant="body2" color="text.secondary"><CircularProgress size={16} /> Loading event details…</Typography>
                  ) : (historyDetail as any)?.error ? (
                    <Alert severity="warning">
                      {typeof (historyDetail as any).error === 'string'
                        ? (historyDetail as any).error
                        : (historyDetail as any).error?.message || 'Failed to load status'}
                    </Alert>
                  ) : allHistoryCategories.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No per-item results available.</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                      {historyDetailSearch.trim() && visibleHistoryCategories.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No items match your search.</Typography>
                      ) : (
                      visibleHistoryCategories.map((category) => {
                        const items = (historyDetailSearch.trim() ? historyFilteredItemsByCategory[category] : historyItemsByCategory[category]) || [];
                        const isOpen = openHistoryCategories.includes(category);
                        const title = category ? `${category.charAt(0).toUpperCase()}${category.slice(1).replace(/([A-Z])/g, ' $1')}` : 'Configs';
                        const hasOldNew = items.some((it: any) => typeof it?.oldValue !== 'undefined' || typeof it?.newValue !== 'undefined');
                        const hasError = items.some((it: any) => it?.error);
                        const isStringResources = category === 'stringResources' || String(category).toLowerCase().includes('string');
                        const hasMultiTarget = historyTargetEnvironments.length >= 2;
                        const hasTargetsWithOldValues = items.some((it: any) => {
                          const targetsArr = it?.targets as TargetEntry[] | undefined;
                          return (targetsArr?.length ?? 0) > 0 && targetsArr?.some((t: TargetEntry) => t && (t as any).oldValue !== undefined);
                        });
                        const hasAnyTargets = items.some((it: any) => (it?.targets?.length ?? 0) > 0);
                        const hasValueColumns = hasOldNew || hasMultiTarget;
                        const showOldValueColumns = hasValueColumns && (hasTargetsWithOldValues || hasMultiTarget || (historyTargetEnvironments.length >= 1 && hasAnyTargets));
                        const sourceEnvName = (historyDetail as any)?.sourceEnv?.environmentName ?? (historyDetail as any)?.sourceEnv?.name ?? (selectedHistoryActivity?.sourceEnv as any)?.environmentName ?? (selectedHistoryActivity?.sourceEnv as any)?.name ?? 'Source';
                        const firstTargetEnvName = historyTargetEnvironments[0]?.environmentName || (historyTargetEnvironments[0] ? `Env ${historyTargetEnvironments[0].id}` : null) || 'Target';
                        const numValueCols = hasMultiTarget && showOldValueColumns
                          ? 1 + historyTargetEnvironments.length * 2
                          : hasMultiTarget
                            ? 1 + historyTargetEnvironments.length
                            : showOldValueColumns
                              ? 3
                              : hasValueColumns ? 2 : 0;
                        const valueColPct = numValueCols > 0 ? Math.min(18, Math.max(8, 60 / numValueCols)) : 0;
                        const keyColWidth = isStringResources ? (hasValueColumns ? 22 : 52) : (hasValueColumns ? 14 : 48);

                        return (
                          <Box key={category} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                            <Box
                              role="button"
                              tabIndex={0}
                              onClick={() => setOpenHistoryCategories((prev) => (prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  setOpenHistoryCategories((prev) => (prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]));
                                }
                              }}
                              sx={{ px: 2, py: 1, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                            >
                              <Typography sx={{ fontWeight: 800 }}>{title} ({items.length})</Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>{isOpen ? '▾' : '▸'}</Typography>
                            </Box>

                            {isOpen && (
                              <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                                <Box component="table" sx={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}>
                                  <colgroup>
                                    {isStringResources ? (
                                      <>
                                        <col style={{ width: `${keyColWidth}%` }} />
                                        {hasValueColumns ? <col style={{ width: `${valueColPct}%` }} /> : null}
                                        {hasMultiTarget && showOldValueColumns
                                          ? historyTargetEnvironments.flatMap((te) => [<col key={`${te.id}-old`} style={{ width: `${valueColPct}%` }} />, <col key={`${te.id}-new`} style={{ width: `${valueColPct}%` }} />])
                                          : hasMultiTarget
                                            ? historyTargetEnvironments.map((te) => <col key={te.id} style={{ width: `${valueColPct}%` }} />)
                                            : showOldValueColumns
                                              ? <>
                                                  <col style={{ width: `${valueColPct}%` }} />
                                                  <col style={{ width: `${valueColPct}%` }} />
                                                </>
                                              : hasValueColumns ? <col style={{ width: `${valueColPct}%` }} /> : null}
                                        <col style={{ width: '10%', minWidth: 70 }} />
                                        {hasError ? <col style={{ width: '8%', minWidth: 60 }} /> : null}
                                      </>
                                    ) : (
                                      <>
                                        <col style={{ width: `${keyColWidth}%` }} />
                                        <col style={{ width: `${keyColWidth}%` }} />
                                        <col style={{ width: '8%', minWidth: 60 }} />
                                        {hasValueColumns ? <col style={{ width: `${valueColPct}%` }} /> : null}
                                        {hasMultiTarget && showOldValueColumns
                                          ? historyTargetEnvironments.flatMap((te) => [<col key={`${te.id}-old`} style={{ width: `${valueColPct}%` }} />, <col key={`${te.id}-new`} style={{ width: `${valueColPct}%` }} />])
                                          : hasMultiTarget
                                            ? historyTargetEnvironments.map((te) => <col key={te.id} style={{ width: `${valueColPct}%` }} />)
                                            : showOldValueColumns
                                              ? <>
                                                  <col style={{ width: `${valueColPct}%` }} />
                                                  <col style={{ width: `${valueColPct}%` }} />
                                                </>
                                              : hasValueColumns ? <col style={{ width: `${valueColPct}%` }} /> : null}
                                        <col style={{ width: '10%', minWidth: 70 }} />
                                        {hasError ? <col style={{ width: '8%', minWidth: 60 }} /> : null}
                                      </>
                                    )}
                                  </colgroup>
                                  <Box component="thead">
                                    <Box component="tr">
                                      {isStringResources ? (
                                        <Box component="th" sx={{ p: 1, textAlign: 'left' }}><Typography variant="caption" sx={{ fontWeight: 700 }}>String key</Typography></Box>
                                      ) : (
                                        <>
                                          <Box component="th" sx={{ p: 1, textAlign: 'left' }}><Typography variant="caption" sx={{ fontWeight: 700 }}>Config Type</Typography></Box>
                                          <Box component="th" sx={{ p: 1, textAlign: 'left' }}><Typography variant="caption" sx={{ fontWeight: 700 }}>Config Key</Typography></Box>
                                          <Box component="th" sx={{ p: 1, textAlign: 'left' }}><Typography variant="caption" sx={{ fontWeight: 700 }}>Level</Typography></Box>
                                        </>
                                      )}
                                      {hasValueColumns ? (
                                        <Box component="th" sx={{ p: 1, textAlign: 'left' }} title={sourceEnvName}>
                                          <Typography variant="caption" sx={{ fontWeight: 700 }}>{sourceEnvName} Value</Typography>
                                        </Box>
                                      ) : null}
                                      {hasMultiTarget && showOldValueColumns
                                        ? historyTargetEnvironments.flatMap((te) => [
                                            <Box key={`${te.id}-old`} component="th" sx={{ p: 1, textAlign: 'left' }}>
                                              <Typography variant="caption" sx={{ fontWeight: 700 }}>{te.environmentName || `Env ${te.id}`} <Box component="span" sx={{ fontSize: 10, fontWeight: 700 }}>(Old Value)</Box></Typography>
                                            </Box>,
                                            <Box key={`${te.id}-new`} component="th" sx={{ p: 1, textAlign: 'left' }}>
                                              <Typography variant="caption" sx={{ fontWeight: 700 }}>{te.environmentName || `Env ${te.id}`} <Box component="span" sx={{ fontSize: 10, fontWeight: 700 }}>(New Value)</Box></Typography>
                                            </Box>,
                                          ])
                                        : hasMultiTarget
                                          ? historyTargetEnvironments.map((te) => (
                                              <Box key={te.id} component="th" sx={{ p: 1, textAlign: 'left' }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700 }}>{te.environmentName || `Env ${te.id}`} Value</Typography>
                                              </Box>
                                            ))
                                          : showOldValueColumns ? (
                                              <>
                                                <Box component="th" sx={{ p: 1, textAlign: 'left' }} title={firstTargetEnvName}>
                                                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{firstTargetEnvName} <Box component="span" sx={{ fontSize: 10, fontWeight: 700 }}>(Old Value)</Box></Typography>
                                                </Box>
                                                <Box component="th" sx={{ p: 1, textAlign: 'left' }} title={`${firstTargetEnvName} New Value`}>
                                                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{firstTargetEnvName} <Box component="span" sx={{ fontSize: 10, fontWeight: 700 }}>(New Value)</Box></Typography>
                                                </Box>
                                              </>
                                            ) : hasValueColumns ? (
                                                <Box component="th" sx={{ p: 1, textAlign: 'left' }} title={firstTargetEnvName}>
                                                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{firstTargetEnvName} Value</Typography>
                                                </Box>
                                              ) : null}
                                      <Box component="th" sx={{ p: 1, textAlign: 'left' }}><Typography variant="caption" sx={{ fontWeight: 700 }}>Status</Typography></Box>
                                      {hasError ? <Box component="th" sx={{ p: 1, textAlign: 'left' }}><Typography variant="caption" sx={{ fontWeight: 700 }}>Error</Typography></Box> : null}
                                    </Box>
                                  </Box>
                                  <Box component="tbody">
                                    {items.map((it: any, idx: number) => {
                                      const getTargetEntry = (te: TargetEnvironment, envIndex: number): TargetEntry | null => {
                                        const targetsArr = it?.targets as TargetEntry[] | undefined;
                                        if (!targetsArr?.length) return null;
                                        const byEnvId = targetsArr.find((x) => Number(x.envId) === Number(te.id));
                                        if (byEnvId) return byEnvId;
                                        const teName = (te.environmentName || '').toLowerCase();
                                        if (teName) {
                                          const byName = targetsArr.find(
                                            (x) => (x.environmentName || '').toLowerCase() === teName
                                          );
                                          if (byName) return byName;
                                        }
                                        if (envIndex >= 0 && targetsArr[envIndex]) {
                                          return targetsArr[envIndex] as TargetEntry;
                                        }
                                        return null;
                                      };
                                      const getTargetValue = (te: TargetEnvironment, envIndex: number) => {
                                        const entry = getTargetEntry(te, envIndex);
                                        if (entry) return entry.targetValue;
                                        return historyTargetEnvironments[0]?.id === te.id ? it?.newValue : undefined;
                                      };
                                      const getTargetOldValue = (te: TargetEnvironment, envIndex: number) => {
                                        const entry = getTargetEntry(te, envIndex);
                                        return entry?.oldValue;
                                      };
                                      return (
                                        <Box component="tr" key={`${String(it?.key ?? idx)}-${idx}`}>
                                          {isStringResources ? (
                                            <Box component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', verticalAlign: 'middle', textAlign: 'left', overflow: 'hidden', minWidth: 0 }}>
                                              <Tooltip title={(it?.metadata as any)?.stringKey ?? it?.key ?? ''} placement="top" enterDelay={300}>
                                                <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace', fontSize: 12, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                  {String((it?.metadata as any)?.stringKey ?? it?.key ?? '')}
                                                </Typography>
                                              </Tooltip>
                                            </Box>
                                          ) : (
                                            <>
                                              <Box component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', verticalAlign: 'middle', textAlign: 'left', maxWidth: 120, overflow: 'hidden' }}>
                                                <Tooltip title={it?.configType ?? it?.category ?? '—'} placement="top" enterDelay={300}>
                                                  <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace', fontSize: 12, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {String(it?.configType ?? it?.category ?? '—')}
                                                  </Typography>
                                                </Tooltip>
                                              </Box>
                                              <Box component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', verticalAlign: 'middle', textAlign: 'left', overflow: 'hidden', minWidth: 0 }}>
                                                <Tooltip title={it?.key ?? ''} placement="top" enterDelay={300}>
                                                  <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace', fontSize: 12, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {String(it?.key ?? '')}
                                                  </Typography>
                                                </Tooltip>
                                              </Box>
                                              <Box component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', verticalAlign: 'middle', textAlign: 'left', overflow: 'hidden', minWidth: 0 }}>
                                                <Tooltip title={it?.level ?? '—'} placement="top" enterDelay={300}>
                                                  <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace', fontSize: 12, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {String(it?.level ?? '—')}
                                                  </Typography>
                                                </Tooltip>
                                              </Box>
                                            </>
                                          )}
                                          {hasValueColumns ? (
                                            <Box component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', verticalAlign: 'top', textAlign: 'left' }}>
                                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary', whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                                {typeof it?.oldValue === 'undefined' ? '' : safeStringify(it.oldValue)}
                                              </Typography>
                                            </Box>
                                          ) : null}
                                          {hasMultiTarget && showOldValueColumns
                                            ? historyTargetEnvironments.flatMap((te, envIndex) => [
                                                <Box key={`${te.id}-old`} component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', verticalAlign: 'top', textAlign: 'left' }}>
                                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary', whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                                    {safeStringify(getTargetOldValue(te, envIndex))}
                                                  </Typography>
                                                </Box>,
                                                <Box key={`${te.id}-new`} component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', verticalAlign: 'top', textAlign: 'left' }}>
                                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                                    {safeStringify(getTargetValue(te, envIndex))}
                                                  </Typography>
                                                </Box>,
                                              ])
                                            : hasMultiTarget
                                              ? historyTargetEnvironments.map((te, envIndex) => (
                                                  <Box key={te.id} component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', verticalAlign: 'top', textAlign: 'left' }}>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                                      {safeStringify(getTargetValue(te, envIndex))}
                                                    </Typography>
                                                  </Box>
                                                ))
                                              : showOldValueColumns ? (
                                                  <>
                                                    <Box component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', verticalAlign: 'top', textAlign: 'left' }}>
                                                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary', whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                                        {(() => {
                                                          const firstTarget = (it?.targets as TargetEntry[] | undefined)?.[0];
                                                          const oldAtTarget = firstTarget?.oldValue ?? (it?.metadata as any)?.oldValueAtTarget;
                                                          return safeStringify(oldAtTarget ?? '');
                                                        })()}
                                                      </Typography>
                                                    </Box>
                                                    <Box component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', verticalAlign: 'top', textAlign: 'left' }}>
                                                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                                        {typeof it?.newValue === 'undefined' ? '' : safeStringify(it.newValue)}
                                                      </Typography>
                                                    </Box>
                                                  </>
                                                ) : hasValueColumns ? (
                                                  <Box component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', verticalAlign: 'top', textAlign: 'left' }}>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                                      {typeof it?.newValue === 'undefined' ? '' : safeStringify(it.newValue)}
                                                    </Typography>
                                                  </Box>
                                                ) : null}
                                          <Box component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', verticalAlign: 'top', textAlign: 'left' }}>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{formatHistoryStatusLabel(it?.status)}</Typography>
                                          </Box>
                                          {hasError ? (
                                            <Box component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', verticalAlign: 'top', textAlign: 'left' }}>
                                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary', whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{it?.error ? String(it.error) : ''}</Typography>
                                            </Box>
                                          ) : null}
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        );
                      }) )}
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
